import { ImportExportRounded } from "@mui/icons-material";
import { Button, Card, CardContent, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useState } from "react";
import {
  AddExistingMetricModal,
  CreateMetricModal,
  MetricsCollectionFilterButton,
  ReorderMetricCollectionModal,
  TeamMetricsGrid,
} from "src/components";
import { useTeamPageLayoutContext } from "../TeamPageLayout";
import { useTeamMetricsSettings } from "./hooks";
import { useAddMetricToCollection, useGetMetricCollection } from "src/http";
import { lexorank } from "src/utilities/lexorank";

const TeamMetricsPage = () => {
  const { team } = useTeamPageLayoutContext();
  const { settings, updateSettings } = useTeamMetricsSettings(team.slug);
  const { data: metrics } = useGetMetricCollection(team.metricCollectionGuid ?? null);
  const { mutate: addMetric } = useAddMetricToCollection();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isAddExistingModalVisible, setIsAddExistingModalVisible] = useState(false);
  const [isReorderModalVisible, setIsReorderModalVisible] = useState(false);

  function handleMetricAdded(metricGuid: string) {
    if (!team.metricCollectionGuid) {
      return;
    }

    addMetric({
      metricCollectionGuid: team.metricCollectionGuid,
      metricGuid: metricGuid,
      sortOrder: lexorank.getNextRank(metrics),
    });
  }

  return (
    <>
      <Stack spacing={{ xs: 0.75, md: 1 }}>
        <Card>
          <CardContent>
            <Stack spacing={1} direction="row" alignItems="center">
              <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                <Stack spacing={0.25}>
                  <Typography variant="h5">Metrics</Typography>
                  <Typography variant="body1">Metrics relevant to the team.</Typography>
                </Stack>
                {team.metricCollectionGuid && team.metricCount > 1 && (
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      variant="tertiary"
                      startIcon={<ImportExportRounded />}
                      onClick={() => setIsReorderModalVisible(true)}
                    >
                      Reorder
                    </Button>

                    <MetricsCollectionFilterButton
                      variant="text"
                      metricCollectionGuid={team.metricCollectionGuid}
                      filterByOption={settings.filterByOption}
                      filterByValue={settings.filterByValue}
                      onFilterChanged={(filterByOption, filterByValue) =>
                        updateSettings({ filterByOption, filterByValue })
                      }
                    />
                  </Stack>
                )}
              </Stack>

              {(team.isAdministrator || team.isMember) && (
                <Button variant="contained" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  Add Metric
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        {team.metricCollectionGuid && (
          <Card>
            <TeamMetricsGrid
              team={team}
              filterByOption={settings.filterByOption}
              filterByValue={settings.filterByValue}
            />
          </Card>
        )}
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setIsCreateModalVisible(true);
          }}
        >
          New
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setIsAddExistingModalVisible(true);
          }}
        >
          Existing
        </MenuItem>
      </Menu>

      <CreateMetricModal
        visible={isCreateModalVisible}
        onAdded={() => setIsCreateModalVisible(false)}
        onCancelled={() => setIsCreateModalVisible(false)}
      />

      <AddExistingMetricModal
        visible={isAddExistingModalVisible}
        onAdded={(metricGuid) => {
          handleMetricAdded(metricGuid);
          setIsAddExistingModalVisible(false);
        }}
        onCancelled={() => setIsAddExistingModalVisible(false)}
        excludedTeamSlug={team.slug}
      />

      {team.metricCollectionGuid && (
        <ReorderMetricCollectionModal
          visible={isReorderModalVisible}
          metricCollectionGuid={team.metricCollectionGuid}
          onSave={() => setIsReorderModalVisible(false)}
          onCancel={() => setIsReorderModalVisible(false)}
        />
      )}
    </>
  );
};

export { TeamMetricsPage };
